import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import {
  PieChart as PieIcon, BarChart3, Layers, RefreshCw,
  AlertCircle, Loader2,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import { formatCurrency, formatNumber, labelReason } from './format';

const REASON_COLORS = [
  'var(--color-primary)',
  'var(--color-accent)',
  'var(--color-warning)',
  'var(--color-error)',
  'var(--color-success)',
  '#8b5cf6',
];

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bb-tip">
      {label && <div className="bb-tip__label">{label}</div>}
      {payload.map((p) => (
        <div key={p.dataKey || p.name} className="bb-tip__row">
          <span className="bb-tip__swatch" style={{ background: p.color || p.fill }} />
          <span className="bb-tip__name">{p.name}</span>
          <span className="bb-tip__val">{formatNumber(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

function ReasonDonut({ reasons }) {
  const data = (reasons || []).map((r, i) => ({
    name: labelReason(r.reason || r.key),
    value: Number(r.count) || 0,
    total_value: Number(r.total_value || r.totalValue || 0),
    color: REASON_COLORS[i % REASON_COLORS.length],
  }));
  const total = data.reduce((s, d) => s + d.value, 0);

  if (total === 0) {
    return <div className="bb-chart bb-chart--empty">No unclassified items found 🎉</div>;
  }

  return (
    <div className="bb-chart">
      <div className="bb-chart__title">Why items are unclassified ({formatNumber(total)} total)</div>
      <div className="bb-chart__row">
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={85}
              paddingAngle={2}
              stroke="var(--color-surface)"
            >
              {data.map((d) => (
                <Cell key={d.name} fill={d.color} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <ul className="bb-legend">
          {data.map((d) => {
            const pct = total > 0 ? (d.value / total) * 100 : 0;
            return (
              <li key={d.name} className="bb-legend__row">
                <span className="bb-legend__swatch" style={{ background: d.color }} />
                <span className="bb-legend__name">{d.name}</span>
                <span className="bb-legend__count">{formatNumber(d.value)}</span>
                <span className="bb-legend__pct">{pct.toFixed(1)}%</span>
                {d.total_value > 0 && (
                  <span className="bb-legend__value">{formatCurrency(d.total_value)}</span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function DepartmentBars({ byDept }) {
  const data = (byDept || [])
    .slice(0, 12)
    .map((d) => ({
      name: d.acronym || d.dept_code || d.dept_name || 'Unknown',
      count: Number(d.count) || 0,
      total_value: Number(d.total_value || 0),
    }));

  if (data.length === 0) {
    return <div className="bb-chart bb-chart--empty">No per-department data.</div>;
  }

  return (
    <div className="bb-chart">
      <div className="bb-chart__title">Unclassified items by department (top {data.length})</div>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} />
          <YAxis tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} />
          <Tooltip content={<ChartTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="count" name="Items" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function TierBreakdown({ byTier }) {
  if (!Array.isArray(byTier) || byTier.length === 0) return null;
  return (
    <div className="bb-tier-grid">
      {byTier.map((t) => (
        <div key={t.tier || t.label} className="bb-tier-card">
          <div className="bb-tier-card__label">{t.tier || t.label}</div>
          <div className="bb-tier-card__value">{formatNumber(t.count)}</div>
          <div className="bb-tier-card__sub">
            {formatCurrency(t.total_value || t.totalValue || 0)} at stake
          </div>
        </div>
      ))}
    </div>
  );
}

function BacklogAnalysisPanel() {
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['backlog-buster', 'analyze'],
    queryFn: () => apiClient.get('/api/backlog-buster/analyze'),
    staleTime: 5 * 60_000,
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['backlog-buster', 'analyze'] });
    refetch();
  };

  return (
    <section className="bb-section">
      <header className="bb-section__head">
        <div className="bb-section__title">
          <PieIcon size={16} /> Backlog Analysis
        </div>
        <button
          className="bb-btn bb-btn--ghost"
          onClick={handleRefresh}
          disabled={isFetching}
          title="Refresh analysis"
        >
          <RefreshCw size={14} className={isFetching ? 'bb-spin' : ''} />
          Refresh Analysis
        </button>
      </header>

      {isLoading && (
        <div className="bb-state"><Loader2 size={18} className="bb-spin" /> Analyzing backlog…</div>
      )}
      {error && (
        <div className="bb-state bb-state--error">
          <AlertCircle size={16} /> Failed to load analysis: {error.message}
        </div>
      )}

      {!isLoading && !error && data && (
        <div className="bb-analysis">
          <ReasonDonut reasons={data.reasons || data.by_reason} />

          <div className="bb-analysis__split">
            <div className="bb-analysis__col">
              <DepartmentBars byDept={data.by_department || data.byDepartment} />
            </div>
            <div className="bb-analysis__col">
              <div className="bb-chart">
                <div className="bb-chart__title">
                  <Layers size={13} /> Value tiers at stake
                </div>
                <TierBreakdown byTier={data.by_tier || data.byTier} />
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default BacklogAnalysisPanel;
